<script setup>
import { computed, ref } from 'vue';
import { format, isToday, isYesterday } from 'date-fns';
import { getFileInfo } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { ATTACHMENT_TYPES } from 'dashboard/components-next/message/constants';
import Avatar from 'next/avatar/Avatar.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import { formatBytes } from 'shared/helpers/FileHelper';

const props = defineProps({
  currentChat: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const searchQuery = ref('');
const currentChatAttachments = useMapGetter('getSelectedChatAttachments');

const messageIndex = computed(() => {
  const messages = useCamelCase(props.currentChat?.messages || [], {
    deep: true,
  });
  return new Map(messages.map(message => [message.id, message]));
});

const normalizeTimestamp = value => {
  const numericValue = Number(value || 0);
  if (!numericValue) return 0;
  return numericValue > 1e12 ? numericValue : numericValue * 1000;
};

const formatGroupLabel = timestamp => {
  if (!timestamp) {
    return t('CONVERSATION.SIDEBAR.ATTACHMENTS.GROUPS.OLDER');
  }

  const date = new Date(timestamp);
  if (isToday(date)) {
    return t('CONVERSATION.SIDEBAR.ATTACHMENTS.GROUPS.TODAY');
  }
  if (isYesterday(date)) {
    return t('CONVERSATION.SIDEBAR.ATTACHMENTS.GROUPS.YESTERDAY');
  }

  return format(date, 'MMM d, yyyy');
};

const allAttachments = computed(() => {
  const attachments = useCamelCase(currentChatAttachments.value || [], {
    deep: true,
  });

  return attachments
    .filter(attachment => {
      return (
        attachment.dataUrl &&
        [
          ATTACHMENT_TYPES.IMAGE,
          ATTACHMENT_TYPES.VIDEO,
          ATTACHMENT_TYPES.AUDIO,
          ATTACHMENT_TYPES.FILE,
        ].includes(attachment.fileType)
      );
    })
    .map(attachment => {
      const relatedMessage = messageIndex.value.get(attachment.messageId) || {};
      const sender = useCamelCase(relatedMessage.sender || {}, { deep: true });
      const createdAt = normalizeTimestamp(
        relatedMessage.createdAt || attachment.createdAt
      );
      const fileInfo = getFileInfo(attachment.dataUrl || '');
      const fileExtension = (
        attachment.extension ||
        fileInfo.type ||
        attachment.fileType ||
        'txt'
      ).toLowerCase();
      const fileName =
        attachment.fileName ||
        fileInfo.name ||
        t('CONVERSATION.SIDEBAR.ATTACHMENTS.UNKNOWN_FILE');
      const metaParts = [
        fileExtension ? fileExtension.toUpperCase() : null,
        attachment.fileSize ? formatBytes(attachment.fileSize, 0) : null,
      ].filter(Boolean);

      return {
        id: attachment.id,
        createdAt,
        dataUrl: attachment.dataUrl,
        fileExtension,
        fileName,
        fileType: attachment.fileType,
        metaText: metaParts.join(' | '),
        previewUrl:
          attachment.fileType === ATTACHMENT_TYPES.IMAGE
            ? attachment.thumbUrl || attachment.dataUrl
            : '',
        senderName: sender.name || '',
        senderThumbnail: sender.thumbnail || sender.avatarUrl || '',
      };
    })
    .sort((left, right) => right.createdAt - left.createdAt);
});

const filteredAttachments = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  if (!query) {
    return allAttachments.value;
  }

  return allAttachments.value.filter(attachment =>
    attachment.fileName.toLowerCase().includes(query)
  );
});

const groupedAttachments = computed(() => {
  const groups = [];
  const groupsMap = new Map();

  filteredAttachments.value.forEach(attachment => {
    const groupLabel = formatGroupLabel(attachment.createdAt);

    if (!groupsMap.has(groupLabel)) {
      const group = { label: groupLabel, items: [] };
      groupsMap.set(groupLabel, group);
      groups.push(group);
    }

    groupsMap.get(groupLabel).items.push(attachment);
  });

  return groups;
});
</script>

<template>
  <div class="flex h-full w-full flex-col bg-n-surface-1">
    <div class="border-b border-n-weak px-3 py-3">
      <div
        class="flex items-center gap-2 rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-2"
      >
        <span class="i-lucide-search size-4 text-n-slate-10" />
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="
            $t('CONVERSATION.SIDEBAR.ATTACHMENTS.SEARCH_PLACEHOLDER')
          "
          class="reset-base h-5 min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-n-slate-12 outline-none ring-0 placeholder:text-n-slate-10"
        />
      </div>
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto px-3 py-3">
      <div
        v-if="!groupedAttachments.length"
        class="flex h-full items-center justify-center text-sm text-n-slate-10"
      >
        {{ $t('CONVERSATION.SIDEBAR.ATTACHMENTS.EMPTY') }}
      </div>

      <template v-else>
        <section
          v-for="group in groupedAttachments"
          :key="group.label"
          class="mb-4 last:mb-0"
        >
          <div class="mb-3 flex justify-center">
            <span
              class="rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-11"
            >
              {{ group.label }}
            </span>
          </div>

          <a
            v-for="attachment in group.items"
            :key="attachment.id"
            :href="attachment.dataUrl"
            target="_blank"
            rel="noreferrer noopener nofollow"
            class="mb-2 flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-n-alpha-2"
          >
            <div
              class="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-n-slate-2"
            >
              <img
                v-if="attachment.previewUrl"
                :src="attachment.previewUrl"
                :alt="attachment.fileName"
                class="h-full w-full object-cover"
              />
              <FileIcon
                v-else
                :file-type="attachment.fileExtension"
                class="text-lg text-n-slate-11"
              />
            </div>

            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-medium text-n-slate-12">
                {{ attachment.fileName }}
              </p>
              <p class="truncate text-xs text-n-slate-10">
                {{ attachment.metaText }}
              </p>
            </div>

            <div class="flex shrink-0 items-center gap-2">
              <Avatar
                v-if="attachment.senderName || attachment.senderThumbnail"
                :name="attachment.senderName"
                :src="attachment.senderThumbnail"
                :size="20"
                rounded-full
              />
              <span class="i-lucide-arrow-up-right size-4 text-n-brand-11" />
            </div>
          </a>
        </section>
      </template>
    </div>
  </div>
</template>
